import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import "normalize.css";
import "./App.css";
import { Toaster } from "react-hot-toast";
import Dashboard from "./newComponents/event/Dashboard";
import EventForm from "./newComponents/event/EventForm";
import EventAttendance from "./newComponents/event/EventAttendance";
import Participants from "./components/participant/Participants";
import Sponsors from "./components/content/sponsors/Sponsors";
import Events from "./newComponents/event/Events";
import Category from "./newComponents/category/Category";
import { Login } from "./components/loginPage/Login";
import Cookies from "js-cookie";
import axios from "axios";
import { FbCallbackHandler } from "./components/loginPage/loginHandelSystem/fbCallbackHandler";
import { Venue } from "./newComponents/venue/Venue";
import EventHashtag from "./newComponents/eventHashtag/EventHashtag";
import Instagram from "./components/participant/Instagram";
import { SponsorInsta } from "./components/content/sponsors/sponsorListTable/SponsorInsta";
import SponsorProfileView from "./components/content/sponsors/sponsorListTable/SponsorProfileView";
import { PrivacyPolicy } from "./components/content/policyPage/PrivacyPolicy";
import { Terms } from "./components/content/policyPage/Terms";
import { FairPolicy } from "./components/content/policyPage/FairPolicy";
import FixedComponents from "./newComponents/FixedComponents";
import { SidebarProvider, useSidebar } from "./context/SidebarContext";
import Wallet from "./newComponents/wallet/Wallet";
import Notifications from "./newComponents/notification/Notification";
import HelpSupport from "./newComponents/helpSupport/HelpSupport";
import Referal from "./newComponents/referal/Referal";
import WinnersList from "./newComponents/winnings/WinnersList";
import FAQManager from "./newComponents/faqs/FAQManager";
import VideoPackage from "./newComponents/videoPackage/VideoPackage";
import CollabTickets from "./newComponents/collabRequest/CollabTickets";
import SponsorInvoices from "./newComponents/sponsorInvoices/SponsorInvoices";
import ParticipantDetail from "./components/participant/ParticipantDetail";
import EventAttachments from "./newComponents/eventAttachments/EventAttachments";
import EventAttachmentDetail from "./newComponents/eventAttachments/EventAttachmentDetail";
import UserApproval from "./newComponents/userApproval/UserApproval";
import AppSettings from "./newComponents/settings/AppSettings";
import RefreshIgToken from "./newComponents/settings/RefreshIgToken";
import EventAutomationConfig from './newComponents/automation/EventAutomationConfig';
import AutomationAnalytics from './newComponents/automation/AutomationAnalytics';
import SubscriptionsManager from './newComponents/subscriptions/SubscriptionsManager';
import SubscriptionPlans from './newComponents/subscriptions/SubscriptionPlans';
import { initObservability } from "./helpers/observability";
import { GenericErrorPage, MaintenancePage } from "./components/common/ErrorViews";

const BuggyComponent = () => {
  throw new Error("Simulated rendering crash caught by React Error Boundary!");
};

// Initialize global error reporting
initObservability();

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize from localStorage for instant auth check
    return localStorage.getItem("isAuthenticated") === "true";
  });
  const [isLoading, setIsLoading] = useState(true);
  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  const checkMaintenance = async () => {
    try {
      const apiDomain = process.env.REACT_APP_API_DOMAIN || "https://myapi-dev-226556767471.asia-south1.run.app";
      const response = await axios.get(`${apiDomain}/api/v2/appsettings/get`);
      const data = response.data;
      if (data && data.maintenanceMode) {
        setMaintenanceActive(true);
        setMaintenanceMsg(data.maintenanceMessage || "Platform is currently undergoing scheduled updates.");
      } else {
        setMaintenanceActive(false);
      }
    } catch (e) {
      console.error("Failed to check maintenance:", e);
    } finally {
      setMaintenanceChecked(true);
    }
  };

  useEffect(() => {
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 15000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  useEffect(() => {
    if (!maintenanceChecked) return;

    if (maintenanceActive) {
      if (
        location.pathname !== "/maintenance" &&
        location.pathname !== "/admin/login" &&
        location.pathname !== "/app-settings" &&
        location.pathname !== "/error" &&
        location.pathname !== "/simulate-crash"
      ) {
        navigate("/maintenance");
      }
    }
  }, [maintenanceActive, maintenanceChecked, location.pathname, navigate]);

  useEffect(() => {
    const checkAuth = () => {
      const authToken = Cookies.get("authToken");
      const fbAuthToken = Cookies.get("fbauthToken");
      const loginTime = Cookies.get("loginTime");

      // Debug logging
      console.log("[App.js] Auth Check:", {
        authToken: !!authToken,
        fbAuthToken: !!fbAuthToken,
        loginTime: loginTime ? new Date(parseInt(loginTime)).toLocaleString() : null,
        currentTime: new Date().toLocaleString()
      });

      // Validate auth: authToken must exist, and loginTime must be within 7 days
      // This matches the cookie expiry set in Login.js (7 days)
      const isLoginTimeValid = loginTime && Date.now() - parseInt(loginTime) < 7 * 24 * 60 * 60 * 1000;
      
      // User is logged in if authToken exists AND loginTime is valid
      const isLoggedIn = authToken && isLoginTimeValid;

      console.log("[App.js] Auth Result:", { isLoggedIn, isLoginTimeValid });
      
      if (isLoggedIn) {
        setIsLoggedIn(true);
        localStorage.setItem("isAuthenticated", "true");
      } else {
        setIsLoggedIn(false);
        localStorage.removeItem("isAuthenticated");
      }

      setIsLoading(false);
    };
    checkAuth();
  }, [location.pathname]);

  // Show loading spinner while checking auth (without sidebar/navbar)
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
      <Routes>
        {/* Public routes without sidebar or auth requirements */}
        <Route path="/auth/facebook/callback" element={<FbCallbackHandler />} />
        <Route path="/maintenance" element={<MaintenancePage />} />
        <Route path="/error" element={<GenericErrorPage error={new Error("This is a simulated generic error. Details of the stack trace are shown here for debugging purposes.")} />} />
        <Route path="/simulate-crash" element={<BuggyComponent />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/fairpolicy" element={<FairPolicy />} />
        <Route path="/fair-policy" element={<FairPolicy />} />
        <Route path="/termcondition" element={<Terms />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-conditions" element={<Terms />} />
        
        {!isLoggedIn ? (
          <>
            <Route path="/admin/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/admin/login" replace />} />
          </>
        ) : (
          <>
            {/* All authenticated routes wrapped with FixedComponents layout */}
            <Route
              path="/*"
              element={
                <SidebarProvider>
                  <FixedComponents />
                  <AuthenticatedContent maintenanceActive={maintenanceActive} />
                </SidebarProvider>
              }
            />
          </>
        )}
      </Routes>
    </div>
  );
}

/**
 * AuthenticatedContent — reads sidebar state from context
 * and applies the correct left padding so pages fill the available width.
 */
function AuthenticatedContent({ maintenanceActive }) {
  const { isMobile, isCollapsed } = useSidebar();
  const navigate = useNavigate();
  return (
    <div
      className={`pt-[80px] transition-all duration-300 ${
        isMobile ? "pl-0" : isCollapsed ? "pl-[60px]" : "pl-[18vw]"
      }`}
    >
      {maintenanceActive && (
        <div className="bg-gradient-to-r from-red-600 to-amber-600 text-white py-3 px-4 text-center font-medium shadow-md flex items-center justify-center gap-3 animate-pulse mb-6 lg:mr-[2vw] rounded-xl border border-red-500">
          <span>⚠️</span>
          <span>System Maintenance Mode is currently ACTIVE. Standard APIs are blocked.</span>
          <button 
            onClick={() => navigate('/app-settings')}
            className="bg-white/20 hover:bg-white/30 text-white px-3 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm transition-all"
          >
            Manage Settings
          </button>
        </div>
      )}
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/admin/login" element={<Navigate to="/" replace />} />
        <Route
          path="/participants/:id/:instagramBusinessAccountId/:isiguser"
          element={<Instagram />}
        />
        <Route path="/participant-detail/:userId" element={<ParticipantDetail />} />
        <Route path="/participants" element={<Participants />} />
        <Route path="/category" element={<Category />} />
        <Route path="/venue" element={<Venue />} />
        <Route path="/eventhashtag" element={<EventHashtag />} />
        <Route path="/sponsors" element={<Sponsors />} />
        <Route path="/sponsor-invoices" element={<SponsorInvoices />} />
        <Route path="/events" element={<Events />} />
        <Route path="/notification" element={<Notifications />} />
        <Route path="/wallet" element={<Wallet />} />
        <Route path="/winnings" element={<WinnersList />} />
        <Route path="/help-support" element={<HelpSupport />} />
        <Route path="/help-support/:ticketId" element={<HelpSupport />} />
        <Route path="/collabtickets" element={<CollabTickets />} />
        <Route path="/referal" element={<Referal />} />
        <Route path="/user-approvals" element={<UserApproval />} />
        <Route path="/event-form" element={<EventForm />} />
        <Route path="/event-form/:eventId" element={<EventForm />} />
        <Route path="/event-attendance/:eventId" element={<EventAttendance />} />
        <Route path="/faqs" element={<FAQManager />} />
        <Route path="/video-package" element={<VideoPackage />} />
        <Route path="/event-attachments" element={<EventAttachments />} />
        <Route path="/event-attachments/:eventId" element={<EventAttachmentDetail />} />
        <Route path="/app-settings" element={<AppSettings />} />
        <Route path="/refresh-ig-token" element={<RefreshIgToken />} />
        <Route path="/event-automation" element={<EventAutomationConfig />} />
        <Route path="/event-automation/:eventId" element={<EventAutomationConfig />} />
        <Route path="/automation-analytics" element={<AutomationAnalytics />} />
        <Route path="/subscriptions" element={<SubscriptionsManager />} />
        <Route path="/subscription-plans" element={<SubscriptionPlans />} />
        <Route
          path="/sponsors/:id/:instagramBusinessAccountId"
          element={<SponsorInsta />}
        />
        <Route
          path="/sponsor/view/:userId"
          element={<SponsorProfileView />}
        />
      </Routes>
    </div>
  );
}

export default App;







// import React, { useEffect, useState } from "react";
// import { Routes, Route, Navigate, useLocation } from "react-router-dom";
// import "normalize.css";
// import "./App.css";

// import Dashboard from "./newComponents/event/Dashboard";
// import EventForm from "./newComponents/event/EventForm";
// import Participants from "./components/participant/Participants";
// import Sponsors from "./components/content/sponsors/Sponsors";
// import Events from "./newComponents/event/Events";
// import Category from "./newComponents/category/Category";
// import { Login } from "./components/loginPage/Login";
// import Cookies from "js-cookie";
// import { FbCallbackHandler } from "./components/loginPage/loginHandelSystem/fbCallbackHandler";
// import { Venue } from "./newComponents/venue/Venue";
// import EventHashtag from "./newComponents/eventHashtag/EventHashtag";
// import Instagram from "./components/participant/Instagram";
// import { SponsorInsta } from "./components/content/sponsors/sponsorListTable/SponsorInsta";
// import { PrivacyPolicy } from "./components/content/policyPage/PrivacyPolicy";
// import { Terms } from "./components/content/policyPage/Terms";
// import { FairPolicy } from "./components/content/policyPage/FairPolicy";
// import FixedComponents from "./newComponents/FixedComponents";
// import Wallet from "./newComponents/wallet/Wallet";
// import Notifications from "./newComponents/notification/Notification";
// import HelpSupport from "./newComponents/helpSupport/HelpSupport";
// import Referal from "./newComponents/referal/Referal";
// import WinnersList from "./newComponents/winnings/WinnersList";
// import FAQManager from "./newComponents/faqs/FAQManager";
// import VideoPackage from "./newComponents/videoPackage/VideoPackage";
// import CollabTickets from "./newComponents/collabRequest/CollabTickets";

// function App() {
//   const location = useLocation();
//   const [isLoggedIn, setIsLoggedIn] = useState(false);

//   // OPTIONAL: remove these placeholders if unused
//   const id = "some_id";
//   const instagramBusinessAccountId = "instagram_business_account_id";
//   const userFbToken = "user_facebook_token";

//   useEffect(() => {
//     const checkAuth = () => {
//       const fbAuthToken = Cookies.get("fbauthToken");
//       const igAuthToken = Cookies.get("igauthtoken");
//       const authToken = Cookies.get("authToken");    // legacy admin login
//       const loginTimeStr = Cookies.get("loginTime"); // number as string
//       const userId = Cookies.get("userId");

//       // Accept social login as sufficient
//       const hasSocial = Boolean(fbAuthToken || igAuthToken);

//       // Fallback: legacy admin session (24h TTL)
//       const sessionDurationMs = 24 * 60 * 60 * 1000;
//       const loginTime = loginTimeStr ? parseInt(loginTimeStr, 10) : 0;
//       const hasAdmin =
//         Boolean(authToken && loginTime) &&
//         Date.now() - loginTime < sessionDurationMs;

//       const loggedIn = hasSocial || hasAdmin;

//       console.log("[App] checkAuth", {
//         fbAuthToken: !!fbAuthToken,
//         igAuthToken: !!igAuthToken,
//         authToken: !!authToken,
//         userId,
//         loginTime,
//         loggedIn,
//       });

//       setIsLoggedIn(loggedIn);
//     };

//     // Re-evaluate on route change (cookies may have changed on callback route)
//     checkAuth();
//   }, [location.pathname, location.search]);

//   return (
//     <div className="App">
//       <FixedComponents />
//       <div className="mainContent">
//         <div className="content">
//           <Routes>
//             {/* Public content routes */}
//             <Route path="/privacypolicy" element={<PrivacyPolicy />} />
//             <Route path="/fairpolicy" element={<FairPolicy />} />
//             <Route path="/termcondition" element={<Terms />} />

//             {/* OAuth callback routes are PUBLIC so they can consume the ticket */}
//             <Route path="/auth/facebook/callback" element={<FbCallbackHandler />} />
//             <Route path="/auth/instagram/callback" element={<FbCallbackHandler />} />

//             {/* Auth routes */}
//             {!isLoggedIn ? (
//               <>
//                 <Route path="/admin/login" element={<Login />} />
//                 {/* Anything else redirects to login */}
//                 <Route path="*" element={<Navigate to="/admin/login" replace />} />
//               </>
//             ) : (
//               <>
//                 {/* Private app routes */}
//                 <Route path="/" element={<Dashboard />} />
//                 <Route path="/participants" element={<Participants />} />
//                 <Route
//                   path="/participants/:id/:instagramBusinessAccountId/:isiguser"
//                   element={<Instagram />}
//                 />
//                 <Route path="/category" element={<Category />} />
//                 <Route path="/venue" element={<Venue />} />
//                 <Route path="/eventhashtag" element={<EventHashtag />} />
//                 <Route path="/sponsors" element={<Sponsors />} />
//                 <Route path="/sponsors/:id/:instagramBusinessAccountId" element={<SponsorInsta />} />
//                 <Route path="/events" element={<Events />} />
//                 <Route path="/notification" element={<Notifications />} />
//                 <Route path="/wallet" element={<Wallet />} />
//                 <Route path="/winnings" element={<WinnersList />} />
//                 <Route path="/helpsupport" element={<HelpSupport />} />
//                 <Route path="/collabtickets" element={<CollabTickets />} />
//                 <Route path="/referal" element={<Referal />} />
//                 <Route path="/event-form" element={<EventForm />} />
//                 <Route path="/faqs" element={<FAQManager />} />
//                 <Route path="/video-package" element={<VideoPackage />} />

//                 {/* Default private landing */}
//                 <Route path="*" element={<Navigate to="/" replace />} />
//               </>
//             )}
//           </Routes>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default App;






